// The Activity Map's (the octopus's) click BI — ONE vocabulary for desktop web, mobile web, iOS and Android,
// written down in `native-apps/shared/activity-map-bi/contract.json`. Every press that opens something or acts
// logs one click with `componentName` = the contract's `name` on `ACTIVITY_MAP_BI_PAGE`; closing logs nothing.
// `test/activity-map-bi-contract.test.ts` reads that file and checks this package's source for every event
// that lists `desktop` or `mobile-web`.
//
// Pure, with type-only imports, so the node mapping below is directly testable (the package's test rule).
import type { Entity } from '@wix/data-tools-generic-events';
import { ENTITY_TYPE } from './bi-fields';
import { ACTIVITY_MAP_HOME_PAGE_NAME } from './bi-page-name';
import type { MapNode } from './home-map/map-layout';

/** The contract's `page`: the Activity Map home's own page name, which its page view reports too. */
export const ACTIVITY_MAP_BI_PAGE = ACTIVITY_MAP_HOME_PAGE_NAME;

/** The two trays: the channels in, and the tools. The contract names them `channels` and `connectors`. */
export type ActivityMapCluster = 'channels' | 'connectors';

/** An agent's entity on the map. The Maestro is the agent `maestro` (the contract's "agent: maestro"). */
export const activityMapAgentEntity = (agentId: string): Entity => ({ id: agentId, type: ENTITY_TYPE.agent });
export const ACTIVITY_MAP_MAESTRO_ENTITY: Entity = activityMapAgentEntity('maestro');

/**
 * The rail's name for an entry. Messages is the Outreach category (the board names it `outreach`, its page is
 * `/contacts`), so it reports `messages`, as the phones' rail row is called.
 */
export const activityMapRailName = (entry: 'brief' | 'tasks' | 'routines' | 'goals' | 'outreach' | 'apps' | 'files'): string =>
  `activity-map-rail:${entry === 'outreach' ? 'messages' : entry}`;

/** What a press on a canvas node reports: the contract's name, plus the agent it is or the chip's slug. */
export interface ActivityMapNodeClick {
  name: string;
  entity?: Entity;
  slug?: string;
}

/**
 * The click a desktop canvas node logs when it OPENS its card, by what the node is: the Maestro, an agent's dot,
 * a chip on either tray, the tools tray's "+" (connect more) or a tray's folded "+N". Anything else the canvas
 * never draws (the brief, outreach, buckets, Spaces) reports nothing — null.
 */
export function activityMapNodeClick(node: Pick<MapNode, 'id' | 'region' | 'slug' | 'folded' | 'action'>): ActivityMapNodeClick | null {
  const cluster: ActivityMapCluster | null =
    node.region === 'channelsIn' ? 'channels' : node.region === 'connectors' ? 'connectors' : null;
  if (node.folded && cluster) return { name: `activity-map-tray-more:${cluster}` };
  if (node.region === 'connectors' && node.action === 'connect') return { name: 'activity-map-connect-more' };
  if (cluster) return { name: `activity-map-chip-card:${cluster}`, ...(node.slug ? { slug: node.slug } : {}) };
  if (node.region === 'centre') return { name: 'activity-map-agent:maestro', entity: ACTIVITY_MAP_MAESTRO_ENTITY };
  if (node.region === 'agents') return { name: 'activity-map-agent:open', entity: activityMapAgentEntity(node.id.replace(/^agent:/, '')) };
  return null;
}
